<template>
    <Dialog
        :value="true"
        :header-text="title"
        :width="1200"
        persistent
        fixed
        @close_modal="$emit('close')"
    >
        <data-table
            :items="items"
            :headers="headers"
            :loading="loader.isLoading"
        >
            <template v-slot:item.action="{ item }">
                <span style="white-space: nowrap">
                    {{ getActionText(item.message) }}
                </span>
            </template>
            <template v-slot:item.created_at="{ item }">
                <span style="white-space: nowrap">
                    {{ formatDateTime(parseSysDateTime(item.created_at)) }}
                </span>
            </template>
            <template v-slot:item.data.date_from="{ item }">
                {{ getInfoText(item) }}
            </template>
            <template v-slot:item.created_by="{ item }">
                <span style="white-space: nowrap">
                    {{ item.created_by }}
                </span>
            </template>
            <template v-slot:item.type="{ item }">
                <div class="d-flex align-center justify-end">
                    <v-tooltip
                        v-if="!item.data.reduce_working_hours && item.related_type === 'calendar'"
                        bottom
                    >
                        <template #activator="{ on }">
                            <div
                                class="mx-1"
                                v-on="on"
                            >
                                <icon
                                    color="n3"
                                    icon="Timer"
                                />
                            </div>
                        </template>
                        {{ $t('Agreed work is still expected') }}
                    </v-tooltip>
                    <v-tooltip
                        v-if="item.data.calendar_type"
                        bottom
                    >
                        <template #activator="{ on }">
                            <div
                                class="mx-1"
                                v-on="on"
                            >
                                <icon
                                    color="n3"
                                    :icon="getCalendarTypeIcon(item.data.calendar_type)"
                                />
                            </div>
                        </template>
                        {{ CalendarModule.translateCalendarType(item.data.calendar_type) }}
                    </v-tooltip>
                </div>
            </template>
        </data-table>
    </Dialog>
</template>

<script>
import Vue from "vue";
import { $t } from "@/i18n";
import {computed, ref, watch, reactive, onMounted} from "@vue/composition-api";
import Dialog from "@/components/Dialog";
import ApiFlexTime from "@/api/report/flextime";
import {
    formatDate,
    parseSysDate,
    parseSysDateTime,
    formatFlextimeDuration,
    formatDateTime, parseSysEndDate, formatTimeDuration, formatDatesFromUntil, formatDaysOrTimeFromDates,
} from "@/plugins/formatting";
import errorDialog from "@/views/ErrorDialog/errorDialog";
import Loader from "@/utility/loader";
import ActivityModule, {CALENDAR_ACTIVITY_TYPES} from "@/modules/activity/activity";
import CalendarModule from '@/modules/calendar/calendar';

export default {
    name: "FlextimeHistoryModal",
    components: {
        Dialog
    },
    props: {
        personId: {
            type: Number,
            required: true
        },
        companyId: {
            type: Number,
            required: true
        },
        personName: {
            type: String,
            required: true
        }
    },
    setup(props, ctx) {
        const items = ref([]);
        const loader = reactive(new Loader());

        onMounted(async () => {
            await loader.withLoader(async () => {
                try {
                    items.value = await ApiFlexTime.getFlextimeHistory(props.personId, props.companyId)
                } catch (e) {
                    errorDialog.showError(e)
                }
            })
        })

        const getActionText = (action) => {
            if (action === ActivityModule.ACTIVITY_TYPE.TYPE_FLEXTIME_ACCEPTED) {
                return $t('Accepted flextime')
            } else if (action === ActivityModule.ACTIVITY_TYPE.TYPE_FLEXTIME_DECLINED) {
                return $t('Declined flextime')
            } else if (action === ActivityModule.ACTIVITY_TYPE.TYPE_FLEXTIME_DELETED) {
                return $t('Cancelled flextime')
            } else if (action === ActivityModule.ACTIVITY_TYPE.TYPE_FLEXTIME_UPDATED) {
                return $t('Updated flextime')
            } else if (action === ActivityModule.ACTIVITY_TYPE.TYPE_CALENDAR_DELETED) {
                return $t('Calendar deleted')
            } else if (action === ActivityModule.ACTIVITY_TYPE.TYPE_CALENDAR_CREATED) {
                return $t('Calendar created')
            } else if (action === ActivityModule.ACTIVITY_TYPE.TYPE_CALENDAR_APPROVED) {
                return $t('Calendar approved')
            } else if (action === ActivityModule.ACTIVITY_TYPE.TYPE_CALENDAR_DECLINED) {
                return $t('Calendar declined')
            } else if (action === ActivityModule.ACTIVITY_TYPE.TYPE_CALENDAR_UPDATED) {
                return $t('Calendar updated')
            } else if (action === ActivityModule.ACTIVITY_TYPE.TYPE_FLEXTIME_BALANCE_UPDATED) {
                return $t('Flextime balance updated')
            }

            return action
        }

        const getFlextimeBalanceUpdatedInfoText = (activity) => {
            let output = []

            if (activity.data) {
                if (activity.data.previous_flextime_credit !== activity.data.current_flextime_credit) {
                    const previousFlextimeCreditFormatted = formatFlextimeDuration(activity.data.previous_flextime_credit)
                    const currentFlextimeCreditFormatted = formatFlextimeDuration(activity.data.current_flextime_credit)

                    const formattedText = $t('Flextime') + ': ' + previousFlextimeCreditFormatted + ' -> ' + currentFlextimeCreditFormatted
                    output.push(formattedText)
                }

                if (activity.data.previous_vacation_credit !== activity.data.current_vacation_credit) {
                    const previousVacationCreditFormatted = activity.data.previous_vacation_credit ? activity.data.previous_vacation_credit / (60 * 60 * 24) : 0
                    const currentVacationCreditFormatted = activity.data.current_vacation_credit ? activity.data.current_vacation_credit / (60 * 60 * 24) : 0

                    const formattedText = $t('Vacation') + ': ' + previousVacationCreditFormatted + ' -> ' + currentVacationCreditFormatted
                    output.push(formattedText)
                }

            } else {
                output = ["-"]
            }

            return output.join(" / ")
        }

        const getFlextimeOrVacationInfoText = (activity) => {
            let output = []

            if (activity.data) {
                if (activity.data.date_from && activity.data.date_until) {
                    let dateFrom = parseSysDateTime(activity.data.date_from)
                    let dateUntil = parseSysDateTime(activity.data.date_until)
                    const negatedEndDate = activity.related_type !== 'flextime'

                    output.push(formatDatesFromUntil(dateFrom, dateUntil, negatedEndDate))
                }

                if (activity.data.duration !== null && activity.data.duration !== undefined) {
                    if (CALENDAR_ACTIVITY_TYPES.includes(activity.type)) {
                        let dateFrom = parseSysDateTime(activity.data.date_from)
                        let dateUntil = parseSysDateTime(activity.data.date_until)

                        output.push(formatDaysOrTimeFromDates(dateFrom, dateUntil))
                    } else {
                        let text = $t('Balance') + ': '

                        if (Number.isInteger(activity.data.duration) && Number.isInteger(activity.data.prev_duration) && activity.data.duration !== activity.data.prev_duration) {
                            text += formatFlextimeDuration(activity.data.prev_duration) + ' -> ' + formatFlextimeDuration(activity.data.duration)
                            output.push(text)
                        } else if (Number.isInteger(activity.data.duration)) {
                            text += formatFlextimeDuration(activity.data.duration)
                            output.push(text)
                        }
                    }
                }

                if (activity.data.comment) {
                    let text = $t('Comment') + ': ' + activity.data.comment
                    output.push(text)
                }

            } else {
                output = ["-"]
            }

            return output.join(" / ")
        }

        const getInfoText = (activity) => {
            if (activity.type === ActivityModule.ACTIVITY_TYPE.TYPE_FLEXTIME_BALANCE_UPDATED) {
                return getFlextimeBalanceUpdatedInfoText(activity)
            } else {
                return getFlextimeOrVacationInfoText(activity)
            }
        }

        const title = computed(() => {
            return $t("History") + " / " + props.personName
        })

        const headers = [
            { value: "created_at", text: $t("Time") },
            { value: "action", text: $t("Action") },
            { value: "data.date_from", text: $t("Info") },
            { value: 'type', text: '', sortable: false },
            { value: "created_by", text: $t("Who") },
        ]

        const getCalendarTypeIcon = (type) => {
            if (type === CalendarModule.CALENDAR_TYPE.VACATION) {
                return 'Palmtree'
            } else if (type === CalendarModule.CALENDAR_TYPE.FLEXTIME_VACATION) {
                return 'Clock'
            } else if (type === CalendarModule.CALENDAR_TYPE.SICK_LEAVE) {
                return 'Frown'
            } else if (type === CalendarModule.CALENDAR_TYPE.PARENTAL_LEAVE) {
                return 'Baby'
            } else if (type === CalendarModule.CALENDAR_TYPE.REMOTE_WORK) {
                return 'Bus'
            }

            return 'Footprints'
        }

        return {
            items,
            loader,
            headers,
            getActionText,
            formatDate,
            parseSysDate,
            formatDateTime,
            getInfoText,
            title,
            parseSysDateTime,
            ActivityModule,
            CalendarModule,
            getCalendarTypeIcon
        }
    }
}
</script>
